import { get } from 'lodash'
import pako from 'pako'
import * as Sentry from '@sentry/vue'

/**
 * 解析查询字符串并返回键值对对象。
 *
 * @param {string} queryString - 待解析的查询字符串。
 * @returns {Object} 返回一个对象，包含解析后的键值对。如果没有提供查询字符串，则返回一个空对象。
 */
export const useQueryParse = (queryString) => {
  if (!queryString) return {}
  try {
    const binaryStr = atob(decodeURIComponent(queryString))
    const unit8Array = new Uint8Array(binaryStr.length)
    for (let i = 0; i < binaryStr.length; i++) {
      unit8Array[i] = binaryStr.charCodeAt(i)
    }
    const decompressStr = pako.inflate(unit8Array, { to: 'string' })
    const json = JSON.parse(decodeURIComponent(decompressStr))
    return json
  } catch (err) {
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      console.error('useQueryParse error', err)
    } else {
      Sentry.captureException(err)
    }
    return {}
  }
}

/**
 * 将对象转换为查询字符串。
 *
 * @param {Object} queryObj - 需要转换为查询字符串的对象。
 * @returns {String|Object} 如果queryObj为空或未定义，则返回一个空字符串；
 * 否则返回转换后的查询字符串。
 */
export const useQueryStringify = (queryObj) => {
  if (!queryObj) return ''
  try {
    const queryStr = JSON.stringify({ aq: queryObj })
    const uint8Arr = pako.deflate(queryStr)
    const base64Str = encodeURIComponent(btoa(String.fromCharCode.apply(null, uint8Arr)))
    return base64Str
  } catch (err) {
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      console.error('useQueryStringify error', err)
    } else {
      Sentry.captureException(err)
    }
    return ''
  }
}

/**
 * 从URL的查询参数中获取指定键值的自动查询值。
 *
 * 该函数用于解析当前URL的查询参数，并从中提取出指定键路径的值。
 * 它通过使用`useQueryParse`钩子来解析查询字符串，并利用`get`函数来获取嵌套对象中的值。
 * 这对于在React应用程序中，根据URL参数动态调整组件状态或内容非常有用。
 *
 * @param {string} keyPath 键路径字符串，用于指定需要获取的值在查询参数对象中的位置。
 * @returns 返回查询参数对象中指定键路径的值。如果键路径不存在，则返回undefined。
 */
export const getAutoQueryValueByKeyPath = (keyPath) => {
  const { aq = {} } = useQueryParse(location.search.split('?')[1])
  return get(aq, keyPath)
}

/**
 * 更新URL查询参数。
 *
 * 该函数用于根据给定的键值对更新当前URL的查询参数部分。它首先解析当前URL的查询字符串，
 * 然后根据提供的键和值来更新或设置查询参数，最后将更新后的URL替换为当前浏览器历史记录的一部分，
 * 这样做可以避免刷新页面，保持页面状态的一致性。
 *
 * @param {string} key 要更新或设置的查询参数的键。
 * @param {string} val 要更新或设置的查询参数的值。
 */
const updateUrl = (key, val) => {
  const obj = useQueryParse(location.search.split('?')[1])
  if (!obj.aq) obj.aq = {}
  obj.aq[key] = val
  const queryString = useQueryStringify(obj.aq)
  history.replaceState({}, '', `${location.pathname}?${queryString}`)
}

/**
 * 使用自动同步URL Query功能。
 * 该函数旨在解析当前URL中的查询参数，并提供一个可响应的对象，该对象的更改会自动反映在URL中。
 * @returns {Object} 返回一个代理对象，该对象的属性与URL查询参数同步。
 */
export const useAutoQuery = () => {
  const { aq = {} } = useQueryParse(location.search.split('?')[1])
  const { b = '', g = {}, f = {}, p = {} } = aq
  const autoQuery = {
    b,
    g,
    f,
    p,
  }

  const autoQueryProxy = new Proxy(autoQuery, {
    set(target, key, value) {
      target[key] = value
      updateUrl(key, value)
      return true
    },
  })

  return autoQueryProxy
}
